import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useStaffMutations } from './useStaff';
import type { StaffClinance, StaffRole } from '@/types/database';

const schema = z.object({
  name: z.string().min(2, 'Nome é obrigatório'),
  email: z.string().email('E-mail inválido'),
  role: z.enum(['admin_clinance', 'gerente_operacao', 'consultor', 'comercial']),
  active: z.boolean(),
});

type FormData = z.infer<typeof schema>;

const ROLE_LABELS: Record<StaffRole, string> = {
  admin_clinance: 'Administrador',
  gerente_operacao: 'Gerente de Operações',
  consultor: 'Consultor',
  comercial: 'Comercial',
};

interface Props {
  open: boolean;
  onClose: () => void;
  staff?: StaffClinance | null;
}

export function StaffFormModal({ open, onClose, staff }: Props) {
  const { create, update } = useStaffMutations();
  const isEdit = Boolean(staff);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { role: 'consultor', active: true },
  });

  useEffect(() => {
    if (staff) {
      reset({
        name: staff.name,
        email: staff.email,
        role: staff.role,
        active: staff.active,
      });
    } else {
      reset({ role: 'consultor', active: true });
    }
  }, [staff, reset]);

  const onSubmit = async (data: FormData) => {
    if (isEdit && staff) {
      await update.mutateAsync({ id: staff.id, ...data });
    } else {
      await create.mutateAsync(data);
    }
    onClose();
  };

  const currentRole = watch('role');
  const currentActive = watch('active');

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-brand text-lg">
            {isEdit ? 'Editar colaborador' : 'Novo colaborador'}
          </DialogTitle>
        </DialogHeader>

        {!isEdit && (
          <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-primary">
            Um e-mail de convite será enviado automaticamente para o colaborador definir a senha e
            acessar o sistema.
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="name">Nome *</Label>
            <Input id="name" placeholder="Ana Oliveira" {...register('name')} />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email">E-mail *</Label>
            <Input
              id="email"
              type="email"
              placeholder="ana@clinance.com.br"
              {...register('email')}
              disabled={isEdit}
            />
            {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
            {isEdit && (
              <p className="text-xs text-muted-foreground">E-mail não pode ser alterado.</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Função *</Label>
            <Select value={currentRole} onValueChange={(v) => setValue('role', v as StaffRole)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(ROLE_LABELS) as StaffRole[]).map((r) => (
                  <SelectItem key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isEdit && (
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select
                value={currentActive ? 'true' : 'false'}
                onValueChange={(v) => setValue('active', v === 'true')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Ativo</SelectItem>
                  <SelectItem value="false">Inativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Salvando…
                </>
              ) : isEdit ? (
                'Salvar alterações'
              ) : (
                'Criar colaborador'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
